<template>
  <SourceFiltersSelector
    :filter="loadSourceGroups"
    :model-value="selectedSourceGroupIds"
    placeholder="Name"
    @item="addSourceGroupFilter"
    @query="query = $event"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { track } from "shared/boot/analytics";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import useSourceFilters from "shared/composables/useSourceFilters";
import type { Source } from "shared/helpers/sourceFilters";
import type { FilterValueItem } from "shared/stores/sources";
import type { Nullable, Stream } from "shared/types";

import SourceFiltersSelector, {
  type SourceFiltersSelectorFilterResponse,
  type SourceFiltersSelectorFilterResponseItem,
} from "./SourceFiltersSelector.vue";

interface Props {
  stream?: Nullable<Stream>;
}

const props = withDefaults(defineProps<Props>(), {
  stream: null,
});

const { selectedSourceGroups } = useSourceFilters();

const { addFilter } = useSourceFilters();
const query = ref<string>("");

const selectedSourceGroupIds = computed<number[]>(() =>
  selectedSourceGroups.value.map(
    (filter) => (filter.value as FilterValueItem).id as number
  )
);

async function loadSourceGroups(
  index: number
): Promise<
  SourceFiltersSelectorFilterResponse<SourceFiltersSelectorFilterResponseItem>
> {
  try {
    const url = props.stream?.id
      ? `streams/${props.stream.id}/sources`
      : "sources";

    const result = await $streemApiV1.get(url, {
      params: {
        q: query.value,
        page: index + 1,
        limit: 50,
        only_source_groups: true,
      },
    });

    const { data, headers } = result;

    return {
      items: data,
      pages: Number(headers["x-total-pages"]),
    };
  } catch (error) {
    ErrorDialog(
      getLocaleText("modals.outreach_sourceGroups_modal.load_contacts_error"),
      error
    );

    return {
      items: [],
      pages: 1,
    };
  }
}

function addSourceGroupFilter(group: Source): void {
  addFilter({
    type: "group",
    value: {
      id: group.id,
      name: group.name,
    },
    group_ids: (group.source_group_and_associated_ids as number[]) ?? [],
  });

  track("Add source group in Advanced Source Filter");
}
</script>
